/**
 * EK1 · Markgräns ur Lantmäteriets fastighetsgränser.
 *
 * Gränserna hämtas som en bild (fria WMS:en, lagret "granser" med egen SLD: heldragna svarta
 * linjer, ingen text). Ytan fylls från tryckpunkterna fram till linjerna, fylls ut så att den når
 * linjernas mitt (grannfastigheter möts då och slås ihop), och konturen görs om till polygoner.
 * Allt här är rena funktioner på pixelrutor så att de kan testas utan karta eller nätverk.
 */

export type Mask = Uint8Array;
export type Pt = [number, number];

/** WMS:en vi läser gränslinjerna från (samma fria tjänst som kartlagret "Fastighetsgränser"). */
export const FASTIGHET_FILL_WMS = 'https://minkarta.lantmateriet.se/map/fastighetsindelning';
/** Heldragna linjer (även de som i kartan är streckade) så att fyllningen inte läcker ut genom luckor. */
export const FASTIGHET_FILL_SLD = '<StyledLayerDescriptor version="1.0.0"><NamedLayer><Name>granser</Name><UserStyle><FeatureTypeStyle><Rule><LineSymbolizer><Stroke><CssParameter name="stroke">#000000</CssParameter><CssParameter name="stroke-width">3</CssParameter></Stroke></LineSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>';

/** Linjebredd i pixlar (SLD:n ovan) och hur mycket barriären vidgas för att stänga små luckor. */
export const LINE_PX = 3;
export const GAP_PX = 1;
/** Ytan vidgas så här mycket tillbaka: genom luckan och halva linjen (grannar möts i linjens mittpixel). */
export const RESTORE_PX = GAP_PX + Math.ceil(LINE_PX / 2);

/** Pixlar som hör till en linje (alfa över tröskeln). */
export function barrierMask(rgba: ArrayLike<number>, w: number, h: number, alphaMin = 64): Mask {
  const m = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) if (rgba[i * 4 + 3] >= alphaMin) m[i] = 1;
  return m;
}

/** Vidgning med en kvadrat (2r+1)×(2r+1), separerbar: först rader, sedan kolumner. */
export function dilate(mask: Mask, w: number, h: number, r: number): Mask {
  if (r <= 0) return mask.slice();
  const tmp = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let last = -Infinity; // senaste satta pixeln till vänster
    for (let x = 0; x < w + r; x++) {
      if (x < w && mask[row + x]) last = x;
      const t = x - r; // pixeln som nu vet allt till höger inom r
      if (t >= 0 && t < w) {
        // satt om någon pixel i [t-r, t+r] är satt; last är den senaste <= t+r
        if (last >= t - r) tmp[row + t] = 1;
      }
    }
  }
  const out = new Uint8Array(w * h);
  for (let x = 0; x < w; x++) {
    let last = -Infinity;
    for (let y = 0; y < h + r; y++) {
      if (y < h && tmp[y * w + x]) last = y;
      const t = y - r;
      if (t >= 0 && t < h && last >= t - r) out[t * w + x] = 1;
    }
  }
  return out;
}

/** Krympning (motsatsen till dilate). Utanför bilden räknas som tomt. */
export function erode(mask: Mask, w: number, h: number, r: number): Mask {
  if (r <= 0) return mask.slice();
  // Kanten räknas som satt så att ytor vid bildkanten inte krymper därifrån.
  const inv = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) inv[i] = mask[i] ? 0 : 1;
  const grown = dilate(inv, w, h, r);
  const out = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) out[i] = grown[i] ? 0 : 1;
  return out;
}

/** Stängning som bryggar smala glipor mellan ytor (t.ex. en kantutjämnad linje mellan grannar). */
export const CLOSE_PX = 2;

/** Närmaste fria pixel (inte barriär) inom radien, eller null. */
export function nearestFree(barrier: Mask, w: number, h: number, x: number, y: number, maxR = 8): Pt | null {
  const xi = Math.round(x), yi = Math.round(y);
  for (let r = 0; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const px = xi + dx, py = yi + dy;
        if (px >= 0 && py >= 0 && px < w && py < h && !barrier[py * w + px]) return [px, py];
      }
    }
  }
  return null;
}

/** Fyllning (4-grannar) från en punkt till barriären. touchesEdge = ytan når bildens kant (inte sluten här). */
export function floodFill(barrier: Mask, w: number, h: number, seed: Pt): { region: Mask; count: number; touchesEdge: boolean } {
  const region = new Uint8Array(w * h);
  const [sx, sy] = seed;
  if (sx < 0 || sy < 0 || sx >= w || sy >= h || barrier[sy * w + sx]) return { region, count: 0, touchesEdge: false };
  const stack = new Int32Array(w * h);
  let sp = 0, count = 0, touchesEdge = false;
  const start = sy * w + sx;
  region[start] = 1; stack[sp++] = start;
  while (sp) {
    const i = stack[--sp];
    count++;
    const x = i % w, y = (i - x) / w;
    if (x === 0 || y === 0 || x === w - 1 || y === h - 1) touchesEdge = true;
    if (x > 0) { const j = i - 1; if (!region[j] && !barrier[j]) { region[j] = 1; stack[sp++] = j; } }
    if (x < w - 1) { const j = i + 1; if (!region[j] && !barrier[j]) { region[j] = 1; stack[sp++] = j; } }
    if (y > 0) { const j = i - w; if (!region[j] && !barrier[j]) { region[j] = 1; stack[sp++] = j; } }
    if (y < h - 1) { const j = i + w; if (!region[j] && !barrier[j]) { region[j] = 1; stack[sp++] = j; } }
  }
  return { region, count, touchesEdge };
}

/** Fyller hål: allt som inte når bildens kant utanför ytan räknas till ytan (enklaver tas med). */
export function fillHoles(region: Mask, w: number, h: number): Mask {
  const outside = new Uint8Array(w * h);
  const stack = new Int32Array(w * h);
  let sp = 0;
  const push = (i: number) => { if (!region[i] && !outside[i]) { outside[i] = 1; stack[sp++] = i; } };
  for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
  while (sp) {
    const i = stack[--sp];
    const x = i % w, y = (i - x) / w;
    if (x > 0) push(i - 1);
    if (x < w - 1) push(i + 1);
    if (y > 0) push(i - w);
    if (y < h - 1) push(i + w);
  }
  const out = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) out[i] = outside[i] ? 0 : 1;
  return out;
}

/**
 * Ytans yttre konturer längs pixelkanterna (hörnkoordinater, x åt höger och y nedåt), en ring per
 * sammanhängande del. Kanterna går medurs runt varje pixel; i hörn där två delar möts diagonalt
 * svänger vi höger så att delarna hålls isär. Hål ska vara ifyllda först (fillHoles).
 */
export function traceRings(region: Mask, w: number, h: number): Pt[][] {
  const at = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && region[y * w + x] === 1;
  const W1 = w + 1;
  // Utgående kanter per hörn: riktning 0=höger,1=ner,2=vänster,3=upp (bitar).
  const out = new Uint8Array(W1 * (h + 1));
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!at(x, y)) continue;
      if (!at(x, y - 1)) out[y * W1 + x] |= 1;            // överkant: (x,y)→(x+1,y)
      if (!at(x + 1, y)) out[y * W1 + x + 1] |= 2;        // högerkant: (x+1,y)→(x+1,y+1)
      if (!at(x, y + 1)) out[(y + 1) * W1 + x + 1] |= 4;  // underkant: (x+1,y+1)→(x,y+1)
      if (!at(x - 1, y)) out[(y + 1) * W1 + x] |= 8;      // vänsterkant: (x,y+1)→(x,y)
    }
  }
  const DX = [1, 0, -1, 0], DY = [0, 1, 0, -1];
  const rings: Pt[][] = [];
  for (let v = 0; v < out.length; v++) {
    while (out[v]) {
      let dir = out[v] & 1 ? 0 : out[v] & 2 ? 1 : out[v] & 4 ? 2 : 3;
      const sx = v % W1, sy = (v - sx) / W1;
      let x = sx, y = sy;
      const ring: Pt[] = [[x, y]];
      for (let guard = 0; guard < out.length * 4; guard++) {
        out[y * W1 + x] &= ~(1 << dir);
        x += DX[dir]; y += DY[dir];
        if (x === sx && y === sy) break;
        const bits = out[y * W1 + x];
        // Föredra höger sväng, sedan rakt fram, sedan vänster.
        const order = [(dir + 1) % 4, dir, (dir + 3) % 4];
        const next = order.find(d => bits & (1 << d));
        if (next == null) break;
        if (next !== dir) ring.push([x, y]);
        dir = next;
      }
      if (ring.length >= 4) rings.push(ring);
    }
  }
  return rings;
}

/** Ytan (skolboksformeln) – positiv för ringar som går medurs i bildkoordinater (y nedåt). */
export function ringArea(ring: Pt[]): number {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i], [x2, y2] = ring[(i + 1) % ring.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

function perpDist(p: Pt, a: Pt, b: Pt): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (!len) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  return Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]) / len;
}

/** Douglas–Peucker för en sluten ring (utan upprepad slutpunkt). */
export function simplifyRing(ring: Pt[], tol: number): Pt[] {
  if (ring.length <= 4) return ring.slice();
  // Dela ringen vid punkten längst bort från första punkten, förenkla båda halvorna.
  let far = 0, farD = -1;
  for (let i = 1; i < ring.length; i++) {
    const d = Math.hypot(ring[i][0] - ring[0][0], ring[i][1] - ring[0][1]);
    if (d > farD) { farD = d; far = i; }
  }
  const dp = (pts: Pt[]): Pt[] => {
    const keep = new Uint8Array(pts.length);
    keep[0] = 1; keep[pts.length - 1] = 1;
    const stack: [number, number][] = [[0, pts.length - 1]];
    while (stack.length) {
      const [s, e] = stack.pop()!;
      let idx = -1, max = tol;
      for (let i = s + 1; i < e; i++) {
        const d = perpDist(pts[i], pts[s], pts[e]);
        if (d > max) { max = d; idx = i; }
      }
      if (idx >= 0) { keep[idx] = 1; stack.push([s, idx], [idx, e]); }
    }
    return pts.filter((_, i) => keep[i]);
  };
  const a = dp(ring.slice(0, far + 1));
  const b = dp([...ring.slice(far), ring[0]]);
  const res = [...a, ...b.slice(1, -1)];
  return res.length >= 3 ? res : ring.slice();
}

// ── Webbmercator (EPSG:3857) ─────────────────────────────────────────────

const R = 6378137;
export function toMerc(lng: number, lat: number): Pt {
  return [R * lng * Math.PI / 180, R * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360))];
}
export function fromMerc(x: number, y: number): Pt {
  return [x / R * 180 / Math.PI, (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * 180 / Math.PI];
}

/** Bildens utsnitt: [minX, minY, maxX, maxY] i EPSG:3857 och storleken i pixlar. */
export interface Raster { bbox: [number, number, number, number]; w: number; h: number }

/** Utsnitt kring punkterna (lng/lat), minst minSpan meter, ungefär metersPerPx, högst maxPx per sida. */
export function rasterFor(points: Pt[], minSpan: number, metersPerPx = 1, maxPx = 4096, minPx = 1024): Raster {
  const m = points.map(([lng, lat]) => toMerc(lng, lat));
  let minX = Math.min(...m.map(p => p[0])), maxX = Math.max(...m.map(p => p[0]));
  let minY = Math.min(...m.map(p => p[1])), maxY = Math.max(...m.map(p => p[1]));
  // Mercatormeter är längre än markmeter (1/cos(lat)); räkna om spannet så att noggrannheten blir rätt.
  const k = 1 / Math.cos(((points.reduce((s, p) => s + p[1], 0) / points.length) * Math.PI) / 180);
  const span = Math.max(maxX - minX, maxY - minY) + minSpan * k;
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  minX = cx - span / 2; maxX = cx + span / 2; minY = cy - span / 2; maxY = cy + span / 2;
  const px = Math.max(minPx, Math.min(maxPx, Math.round(span / (metersPerPx * k))));
  return { bbox: [minX, minY, maxX, maxY], w: px, h: px };
}

export function wmsUrl(r: Raster): string {
  const p = new URLSearchParams({
    service: 'WMS', request: 'GetMap', version: '1.1.1', layers: 'granser', styles: '', format: 'image/png',
    transparent: 'true', srs: 'EPSG:3857', bbox: r.bbox.join(','), width: String(r.w), height: String(r.h),
    sld_body: FASTIGHET_FILL_SLD,
  });
  return `${FASTIGHET_FILL_WMS}?${p.toString()}`;
}

export function lngLatToPx(r: Raster, lng: number, lat: number): Pt {
  const [x, y] = toMerc(lng, lat);
  return [((x - r.bbox[0]) / (r.bbox[2] - r.bbox[0])) * r.w, ((r.bbox[3] - y) / (r.bbox[3] - r.bbox[1])) * r.h];
}
export function pxToLngLat(r: Raster, px: number, py: number): Pt {
  return fromMerc(r.bbox[0] + (px / r.w) * (r.bbox[2] - r.bbox[0]), r.bbox[3] - (py / r.h) * (r.bbox[3] - r.bbox[1]));
}

export type FillResult =
  | { ok: true; polygons: Pt[][]; areaHa: number }      // ringar i [lng, lat], öppna (utan upprepad startpunkt)
  | { ok: false; reason: 'edge' | 'line' | 'empty' };

/**
 * Hela kedjan på en hämtad bild: fyll från varje tryckpunkt, slå ihop, vidga till linjernas mitt,
 * fyll hål, spåra konturerna, förenkla (tol pixlar) och räkna om till lng/lat.
 * 'edge' = någon yta når bildens kant (hämta ett större utsnitt), 'line' = tryckt mitt på en linje.
 */
export function fillToPolygons(rgba: ArrayLike<number>, r: Raster, seeds: Pt[], tol = 1): FillResult {
  const { w, h } = r;
  const barrier = dilate(barrierMask(rgba, w, h), w, h, GAP_PX);
  const union = new Uint8Array(w * h);
  for (const [lng, lat] of seeds) {
    const [x, y] = lngLatToPx(r, lng, lat);
    const s = nearestFree(barrier, w, h, x, y);
    if (!s) return { ok: false, reason: 'line' };
    if (union[s[1] * w + s[0]]) continue; // redan fylld (samma fastighet)
    const f = floodFill(barrier, w, h, s);
    if (f.touchesEdge) return { ok: false, reason: 'edge' };
    for (let i = 0; i < w * h; i++) if (f.region[i]) union[i] = 1;
  }
  const full = fillHoles(erode(dilate(union, w, h, RESTORE_PX + CLOSE_PX), w, h, CLOSE_PX), w, h);
  const rings = traceRings(full, w, h).filter(rg => ringArea(rg) > 0);
  if (!rings.length) return { ok: false, reason: 'empty' };
  const mPerPx = ((r.bbox[2] - r.bbox[0]) / w) * Math.cos((pxToLngLat(r, w / 2, h / 2)[1] * Math.PI) / 180);
  let px2 = 0;
  for (const rg of rings) px2 += ringArea(rg);
  const polygons = rings.map(rg => simplifyRing(rg, tol).map(([x, y]) => pxToLngLat(r, x, y)));
  return { ok: true, polygons, areaHa: (px2 * mPerPx * mPerPx) / 10_000 };
}

/** Markgränsen som FeatureCollection: en huntarea_border per del; delområden (sub_area) behålls. */
export function boundaryFeatureCollection(polygons: Pt[][], name: string, existing?: string | null): string {
  let keep: unknown[] = [];
  try {
    const gj = existing ? JSON.parse(existing) : null;
    if (gj?.type === 'FeatureCollection') keep = (gj.features ?? []).filter((f: any) => f?.properties?.type && f.properties.type !== 'huntarea_border');
  } catch { /* trasig gräns: skrivs över */ }
  const borders = polygons.map(ring => ({
    type: 'Feature',
    properties: { type: 'huntarea_border', name },
    // Medurs i bilden (y nedåt) blir moturs i lng/lat, som GeoJSON vill ha yttre ringar.
    geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] },
  }));
  return JSON.stringify({ type: 'FeatureCollection', features: [...borders, ...keep] });
}
